// 0026: Visual Category Gallery page. Serves BOTH /gallery (root grid of parent
// categories) and /gallery/:categoryId (drill into one category's item groups).
//
// Reads the categories ALREADY loaded into Redux by AppBootstrap — it adds NO network
// call and NO backend dependency. Traversal/breadcrumb go through the generic helpers
// in utils/categories/categoryTree so a future deep tree is a data swap, not a rewrite.
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import CategoryGalleryGrid from "../../components/categories/gallery/CategoryGalleryGrid";
import CategoryGalleryBreadcrumb from "../../components/categories/gallery/CategoryGalleryBreadcrumb";
import { SkeletonCardGrid } from "../../components/general/skeleton";
import useDelayedFlag from "../../hooks/useDelayedFlag";
import { getRootNodes, getChildren, getNodeName, findCategoryById, resolveTileRoute } from "../../utils/categories/categoryTree";

import "../../styles/pages/categories/categoryGalleryPageComponent.scss";

const ROOT_CRUMB = { label: "קטגוריות", to: "/gallery" };

export const CategoryGalleryPageComponent = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const categories = useSelector((state) => state.categoryReducer.categories);

  // Categories hydrate once via AppBootstrap. Until then show a skeleton (delayed to
  // avoid a flash). `categories` is { items: [...] } once present.
  const isHydrating = !categories || !categories.items;
  const showSkeleton = useDelayedFlag(isHydrating);

  if (isHydrating) {
    return (
      <div className="categoryGalleryPage">
        {showSkeleton ? <SkeletonCardGrid /> : null}
      </div>
    );
  }

  // ----- Drill view: /gallery/:categoryId -----
  if (categoryId) {
    const category = findCategoryById(categories, categoryId);

    // Unknown / stale id (e.g. deep-link to a deleted category) — graceful, no crash.
    if (!category) {
      return (
        <div className="categoryGalleryPage">
          <CategoryGalleryBreadcrumb trail={[ROOT_CRUMB]} />
          <div className="categoryGalleryEmpty">
            הקטגוריה לא נמצאה.{" "}
            <button type="button" className="galleryBackLink" onClick={() => navigate("/gallery")}>
              חזרה לכל הקטגוריות
            </button>
          </div>
        </div>
      );
    }

    const children = getChildren(category);
    const trail = [ROOT_CRUMB, { label: getNodeName(category) }];

    return (
      <div className="categoryGalleryPage">
        <CategoryGalleryBreadcrumb trail={trail} />
        <h1 className="galleryHeading">{getNodeName(category)}</h1>
        <CategoryGalleryGrid nodes={children} onNodeClick={(node) => navigate(resolveTileRoute(node, category))} />
      </div>
    );
  }

  // ----- Root view: /gallery -----
  const rootNodes = getRootNodes(categories);

  return (
    <div className="categoryGalleryPage">
      <CategoryGalleryBreadcrumb trail={[{ label: "קטגוריות" }]} />
      <h1 className="galleryHeading">קטגוריות</h1>
      <CategoryGalleryGrid nodes={rootNodes} onNodeClick={(node) => navigate(resolveTileRoute(node, null))} />
    </div>
  );
};

export default CategoryGalleryPageComponent;
